import React, { useState, useEffect, useContext, useCallback } from "react";
import {
  Segment,
  Label,
  Input,
  Icon,
  Popup,
  Button,
  Confirm,
  Image,
  Grid,
  GridRow,
  GridColumn,
} from "semantic-ui-react";
import { ENV, buildStorageUrl } from "../../../../../../../utils";
import { AudioUploader } from "../../../../../../Shared";
import { useDropzone } from "react-dropzone";
import { SlidesContextEdit } from "../../../../../../../contexts/MediaContext";
import "../TemplateManager.scss";

export const Smu04 = React.memo((props) => {
  const { index, slideId, onRemoveSlideByIndex } = props;
  const { slidesActiveSessionEdit } = useContext(SlidesContextEdit);
  const [showConfirm, setShowConfirm] = useState(false);
  const [title, setTitle] = useState("");
  const [titleAudio, setTitleAudio] = useState("");
  const [image, setImage] = useState("");
  const [audio, setAudio] = useState(null);
  const [titleAudio2, setTitleAudio2] = useState("");
  const [image2, setImage2] = useState("");
  const [audio2, setAudio2] = useState(null);
  const [titleAudio3, setTitleAudio3] = useState("");
  const [image3, setImage3] = useState("");
  const [audio3, setAudio3] = useState(null);

  const onOpenCloseConfirm = () => setShowConfirm((prevState) => !prevState);

  /**
   * Synchronizes the component state with the content of the currently selected slide.
   *
   * This `useEffect` hook is executed whenever the `slideId` changes. It searches the `slidesActiveSessionEdit`
   * array for a slide that matches the `slideId`. If such a slide is found and it contains content, the hook
   * updates the component's state with the title, images, titles, and audios for the first three audio objects
   * in the slide's content.
   *
   * Specifically, it performs the following actions:
   * - Sets the `title` state with the slide's title.
   * - For each of the first three audio objects in the slide's content:
   *   - Sets the `image`, `titleAudio`, and `audio` states with the respective properties of the audio object.
   *
   * This ensures that the component accurately represents the content of the current slide, allowing for
   * effective editing and previewing within an active session.
   *
   * @dependency slideId - The ID of the current slide, changes to which trigger the hook.
   */
  useEffect(() => {
    const slide = slidesActiveSessionEdit.find(
      (slide) => slide._id === slideId
    );
    if (slide && slide.content) {
      setTitle(slide.content.title);
      setImage(slide.content.audios[0].image);
      setTitleAudio(slide.content.audios[0].title);
      setAudio(slide.content.audios[0].audio);
      setImage2(slide.content.audios[1].image);
      setTitleAudio2(slide.content.audios[1].title);
      setAudio2(slide.content.audios[1].audio);
      setImage3(slide.content.audios[2].image);
      setTitleAudio3(slide.content.audios[2].title);
      setAudio3(slide.content.audios[2].audio);
    }
  }, [slideId]);

  /**
   * Updates the content of a specific slide within the `slidesActiveSessionEdit` array based on state changes.
   *
   * This `useEffect` hook is triggered when there are changes to the states related to the slide's title, images, title audios, and audios for the first three audio objects. It searches for the slide matching the `slideId` within the `slidesActiveSessionEdit` array. If the slide is found, it updates the slide's content with the new values for the title and for each of the first three audio objects (image, title, and audio).
   *
   * The process is as follows:
   * - Finds the index of the slide with the matching `slideId`.
   * - If the slide is found (i.e., `slideIndex` is not -1), updates the slide's title and the properties of the first three audio objects (image, title, audio) with the new state values.
   *
   * This ensures that any changes made to the slide's title or the properties of the first three audio objects are immediately reflected within the `slidesActiveSessionEdit` array, keeping the session data up-to-date with the user's edits.
   *
   * @dependency title - The current title value to be updated for the slide.
   * @dependency image - The current image value for the first audio object.
   * @dependency titleAudio - The current title for the first audio object.
   * @dependency audio - The current audio file for the first audio object.
   * @dependency image2 - The current image value for the second audio object.
   * @dependency titleAudio2 - The current title for the second audio object.
   * @dependency audio2 - The current audio file for the second audio object.
   * @dependency image3 - The current image value for the third audio object.
   * @dependency titleAudio3 - The current title for the third audio object.
   * @dependency audio3 - The current audio file for the third audio object.
   */
  useEffect(() => {
    const slideIndex = slidesActiveSessionEdit.findIndex(
      (slide) => slide._id === slideId
    );
    if (slideIndex !== -1) {
      slidesActiveSessionEdit[slideIndex].content.title = title;
      slidesActiveSessionEdit[slideIndex].content.audios[0].image = image;
      slidesActiveSessionEdit[slideIndex].content.audios[0].title = titleAudio;
      slidesActiveSessionEdit[slideIndex].content.audios[0].audio = audio;
      slidesActiveSessionEdit[slideIndex].content.audios[1].image = image2;
      slidesActiveSessionEdit[slideIndex].content.audios[1].title = titleAudio2;
      slidesActiveSessionEdit[slideIndex].content.audios[1].audio = audio2;
      slidesActiveSessionEdit[slideIndex].content.audios[2].image = image3;
      slidesActiveSessionEdit[slideIndex].content.audios[2].title = titleAudio3;
      slidesActiveSessionEdit[slideIndex].content.audios[2].audio = audio3;
    }
  }, [
    title,
    image,
    titleAudio,
    audio,
    image2,
    titleAudio2,
    audio2,
    image3,
    titleAudio3,
    audio3,
  ]);

  /**
   * Event handlers and configurations for managing slide content updates in Smu04.js.
   *
   * `handleTitleChange`:
   * - Listens for changes in the slide title input field.
   * - Updates the `title` state, trimming leading whitespace from the input value.
   *
   * `handleTitleAudioChange`, `handleTitleAudio2Change`, `handleTitleAudio3Change`:
   * - Listen for changes in the title audio input fields for the first, second, and third audio objects respectively.
   * - Update their respective states (`titleAudio`, `titleAudio2`, `titleAudio3`), trimming leading whitespace from the input values.
   *
   * `onDrop`, `onDrop2`, `onDrop3`:
   * - Callbacks for handling file drops on the respective image upload areas.
   * - Each accepts the first file from the dropped files array and updates the state (`image`, `image2`, `image3`) with a URL representing the file.
   *
   * `useDropzone` configurations (`getRootProps1`, `getInputProps1`, `getRootProps2`, `getInputProps2`, `getRootProps3`, `getInputProps3`):
   * - Configure the dropzones for accepting JPEG and PNG images.
   * - Utilize the respective `onDrop` callbacks to handle file drops.
   *
   * These functions and configurations facilitate the dynamic updating of slide content, including the title, title audios, and images, allowing for efficient editing within the component.
   */
  const handleTitleChange = (event) => {
    setTitle(event.target.value.trimStart());
  };

  const handleTitleAudioChange = (event) => {
    setTitleAudio(event.target.value.trimStart());
  };

  const handleTitleAudio2Change = (event) => {
    setTitleAudio2(event.target.value.trimStart());
  };

  const handleTitleAudio3Change = (event) => {
    setTitleAudio3(event.target.value.trimStart());
  };

  const onDrop = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0];
    setImage(URL.createObjectURL(file));
  });

  const onDrop2 = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0];
    setImage2(URL.createObjectURL(file));
  });

  const onDrop3 = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0];
    setImage3(URL.createObjectURL(file));
  });

  const { getRootProps: getRootProps1, getInputProps: getInputProps1 } =
    useDropzone({
      accept: "image/jpeg, image/png",
      onDrop: onDrop,
    });

  const { getRootProps: getRootProps2, getInputProps: getInputProps2 } =
    useDropzone({
      accept: "image/jpeg, image/png",
      onDrop: onDrop2,
    });

  const { getRootProps: getRootProps3, getInputProps: getInputProps3 } =
    useDropzone({
      accept: "image/jpeg, image/png",
      onDrop: onDrop3,
    });

  return (
    <>
      <Segment raised className="media-slide">
        <div className="media-slide__header">
          <div className="media-slide__header">
            <Label attached="top left">
              Hoja {index + 1} de {slidesActiveSessionEdit.length}
            </Label>
          </div>
          <div className="media-slide__button">
            <Popup
              content="Eliminar hoja"
              trigger={
                <Button
                  compact
                  color="grey"
                  icon="trash alternate outline"
                  onClick={onOpenCloseConfirm}
                />
              }
            />
          </div>
        </div>
        <div className="media-slide__input">
          <div className="media-slide__label-div">
            <Icon name="bookmark" />
            <h5 className="media-slide__label">Título:</h5>
          </div>
          <Input
            fluid
            className="media-slide__input-title"
            name="title"
            value={title}
            onChange={handleTitleChange}
          />
        </div>
        <div className="row">
          <div className="media-slide__label-div">
            <Icon name="play circle outline" />
            <h5 className="media-slide__label">Contenido:</h5>
          </div>

          <Grid columns={3} divided>
            <GridRow>
              <GridColumn>
                <div className="media-slide__image-audio" {...getRootProps1()}>
                  <input {...getInputProps1()} />
                  <Image
                    src={image?.startsWith('blob:') ? image : (buildStorageUrl(image) || image)}
                    rounded
                    fluid
                  />
                </div>
                <div className="media-slide__label-div">
                  <h5 className="media-slide__label">Título del audio:</h5>
                </div>
                <Input
                  fluid
                  className="media-slide__input"
                  name="titleAudio"
                  value={titleAudio}
                  onChange={handleTitleAudioChange}
                />
                <AudioUploader audio={audio} setAudio={setAudio} />
              </GridColumn>
              <GridColumn>
                <div className="media-slide__image-audio" {...getRootProps2()}>
                  <input {...getInputProps2()} />
                  <Image
                    src={image2?.startsWith('blob:') ? image2 : (buildStorageUrl(image2) || image2)}
                    rounded
                    fluid
                  />
                </div>
                <div className="media-slide__label-div">
                  <h5 className="media-slide__label">Título del audio:</h5>
                </div>
                <Input
                  fluid
                  className="media-slide__input"
                  name="titleAudio2"
                  value={titleAudio2}
                  onChange={handleTitleAudio2Change}
                />
                <AudioUploader audio={audio2} setAudio={setAudio2} />
              </GridColumn>
              <GridColumn>
                <div className="media-slide__image-audio" {...getRootProps3()}>
                  <input {...getInputProps3()} />
                  <Image
                    src={image3?.startsWith('blob:') ? image3 : (buildStorageUrl(image3) || image3)}
                    rounded
                    fluid
                  />
                </div>
                <div className="media-slide__label-div">
                  <h5 className="media-slide__label">Título del audio:</h5>
                </div>
                <Input
                  fluid
                  className="media-slide__input"
                  name="titleAudio3"
                  value={titleAudio3}
                  onChange={handleTitleAudio3Change}
                />
                <AudioUploader audio={audio3} setAudio={setAudio3} />
              </GridColumn>
            </GridRow>
          </Grid>
        </div>
      </Segment>
      <Confirm
        confirmButton="Aceptar"
        cancelButton="Cancelar"
        open={showConfirm}
        onCancel={onOpenCloseConfirm}
        onConfirm={() => onRemoveSlideByIndex(index)}
        content={`¿Deseas eliminar esta hoja?`}
        size="mini"
      />
    </>
  );
});
